import { useState } from 'react';
import { Phone, MapPin, Clock, CheckCircle, Loader2, Wind, Sparkles } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    vehicle: '',
    message: '',
    cabinFilterUpgrade: false,
    sanitiserUpgrade: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitStatus('success');
      setFormData({ 
        name: '', 
        email: '', 
        phone: '', 
        vehicle: '', 
        message: '',
        cabinFilterUpgrade: false,
        sanitiserUpgrade: false,
      });
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      label: 'Phone',
      value: '01323 643333',
      href: 'tel:01323643333',
    },
    {
      icon: MapPin,
      label: 'Address',
      value: 'Unit 2, Hawthorn Road, Eastbourne BN23 6QA',
    },
    {
      icon: Clock,
      label: 'Hours',
      value: 'Monday - Friday: 8am - 5pm',
    },
  ];

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-slate-100 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-teal-600 font-medium text-sm uppercase tracking-wider">
              Get in Touch
            </span>
            <h1 className="text-4xl font-bold text-slate-900 mt-3 mb-6">
              Contact Us
            </h1>
            <p className="text-lg text-slate-600">
              Have a question or want to book an appointment? Fill out the form below or give us a call. We'll get back to you as soon as possible.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Send Us a Message</h2>
              
              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3" data-testid="form-success">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-green-800">Message sent successfully!</p>
                    <p className="text-green-700 text-sm">We'll get back to you as soon as possible.</p>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg" data-testid="form-error">
                  <p className="font-medium text-red-800">Something went wrong</p>
                  <p className="text-red-700 text-sm">Please try again or call us directly.</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                      placeholder="Your name"
                      data-testid="input-name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                      placeholder="your@email.com"
                      data-testid="input-email"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2">
                      Phone
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                      placeholder="Your phone number"
                      data-testid="input-phone"
                    />
                  </div>
                  <div>
                    <label htmlFor="vehicle" className="block text-sm font-medium text-slate-700 mb-2">
                      Vehicle (Make/Model/Year)
                    </label>
                    <input
                      type="text"
                      id="vehicle"
                      name="vehicle"
                      value={formData.vehicle}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                      placeholder="e.g. Ford Focus 2019"
                      data-testid="input-vehicle"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors resize-none"
                    placeholder="Please describe your air conditioning issue or enquiry..."
                    data-testid="input-message"
                  />
                </div>

                {/* Optional Upgrades Section */}
                <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-sm font-medium text-slate-900">Optional Upgrades</span>
                    <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full">Recommended</span>
                  </div>
                  <p className="text-sm text-slate-600 mb-4">
                    These optional services can improve cabin air quality and system hygiene. Tick any you'd like to enquire about.
                  </p>
                  
                  <div className="space-y-4">
                    {/* Cabin Filter Upgrade */}
                    <label className="flex items-start gap-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        name="cabinFilterUpgrade"
                        checked={formData.cabinFilterUpgrade}
                        onChange={handleChange}
                        className="mt-1 w-5 h-5 text-teal-600 border-slate-300 rounded focus:ring-teal-500"
                        data-testid="checkbox-cabin-filter"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Wind className="w-4 h-4 text-teal-600" />
                          <span className="font-medium text-slate-900 group-hover:text-teal-600 transition-colors">
                            Cabin (Pollen) Filter Replacement
                          </span>
                        </div>
                        <p className="text-sm text-slate-500 mt-1">
                          Improves airflow and ensures clean, fresh air inside the vehicle. Recommended if your filter hasn't been changed recently.
                        </p>
                      </div>
                    </label>

                    {/* Sanitiser Treatment Upgrade */}
                    <label className="flex items-start gap-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        name="sanitiserUpgrade"
                        checked={formData.sanitiserUpgrade}
                        onChange={handleChange}
                        className="mt-1 w-5 h-5 text-teal-600 border-slate-300 rounded focus:ring-teal-500"
                        data-testid="checkbox-sanitiser"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-teal-600" />
                          <span className="font-medium text-slate-900 group-hover:text-teal-600 transition-colors">
                            Air Conditioning Sanitiser Treatment
                          </span>
                        </div>
                        <p className="text-sm text-slate-500 mt-1">
                          Disinfects internal components and eliminates odours. Recommended if you notice musty smells when the AC is on.
                        </p>
                      </div>
                    </label>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full sm:w-auto px-8 py-3 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="submit-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Message'
                  )}
                </button>
              </form>
            </div>

            {/* Contact Info Sidebar */}
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Contact Information</h2>
              <div className="space-y-6">
                {contactInfo.map((item) => (
                  <div key={item.label} className="flex gap-4">
                    <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-teal-600" />
                    </div>
                    <div>
                      <div className="font-medium text-slate-900">{item.label}</div>
                      {item.href ? (
                        <a href={item.href} className="text-slate-600 hover:text-teal-600 transition-colors">
                          {item.value}
                        </a>
                      ) : (
                        <div className="text-slate-600">{item.value}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Quick Response */}
              <div className="mt-8 p-6 bg-slate-50 rounded-xl">
                <h3 className="font-semibold text-slate-900 mb-3">Prefer to Call?</h3>
                <p className="text-slate-600 text-sm mb-4">
                  For urgent enquiries or if you'd prefer to speak to someone directly, give us a call during business hours.
                </p>
                <a
                  href="tel:01323643333"
                  className="inline-flex items-center gap-2 text-teal-600 font-medium hover:text-teal-700"
                  data-testid="sidebar-phone-link"
                >
                  <Phone className="w-4 h-4" />
                  01323 643333
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Placeholder */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-slate-900">Find Us</h2>
            <p className="text-slate-600 mt-2">Unit 2, Hawthorn Road, Eastbourne BN23 6QA</p>
          </div>
          <div className="bg-slate-200 rounded-xl h-80 flex items-center justify-center">
            <div className="text-center">
              <MapPin className="w-12 h-12 text-slate-400 mx-auto mb-3" />
              <p className="text-slate-500">Eastbourne, East Sussex</p>
              <a
                href="https://maps.google.com/?q=Unit+2+Hawthorn+Road+Eastbourne+BN23+6QA"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-4 text-teal-600 font-medium hover:text-teal-700"
              >
                Open in Google Maps
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
